import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react";

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal.jsx";
import DebugAccordion from "../../components/DebugAccordion"
import ProfileNav from "./components/ProfileNav"
import ProfileTools from "./components/ProfileTools.jsx";
import PageTitle from "../../components/PageTitle.jsx"
import { addNotification, NotificationTypes } from "../../stores/notification-store.js";
import { isEmptyObject } from '../../utils/utils'
import { UpdateProfile } from "../../utils/api.js";
import { NOT_FOUND } from "../../utils/constants.js";

import useProfile from "../../hooks/useProfile.js"
import useProfileSchema, { profileSchema } from "../../hooks/useProfileSchema.js"
import useSchemaManifest from "../../hooks/useSchemaManifest.js"

export default function ProfileRawEdit() {

    const navigate = useNavigate()
    const { beId, profileId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()
    const { manifest } = useSchemaManifest()

    const { profile, loading: profileLoading, isLoaded: profileIsLoaded, modified, ccschema } = useProfile(beId, profileId, config, accessToken)
    const editableProfile = useSignal('')
    const profileRows = useComputed(() => {
        let text = JSON.stringify(profile.value.componentProfile.config, null, 4)
        let textArray = text.split('\n')
        return textArray.length + 1
    })
    const { loading: schemaLoading, isLoaded: schemaIsLoaded, version: profileVersion } = useProfileSchema(beId, ccschema.value, manifest.value, config, accessToken)

    const showCancelModal = useSignal(false)
    const isFormEdited = useSignal(false)
    const accordionData = [
        { name: 'Original Profile Data', data: profile },
        { name: 'Profile Schema', data: profileSchema },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: manifest }
    ]

    const handleFormSubmit = () => {
        try {
            // build the full config data including apiInterface and deviceConfig data to save back to server
            let data = {
                id: profile.value.componentProfile.id,
                name: profile.value.componentProfile.name,
                businessEntityId: profile.value.componentProfile.businessEntityId,
                config: JSON.parse(editableProfile.value),
            }

            console.log(data)
            UpdateProfile(beId, profileId, data, config, accessToken)
                .then(result => {
                    modified.value = true
                    profile.value = result
                    addNotification('Profile was successfully updated.', NotificationTypes.SUCCESS, true)
                    navigate('../')
                })
                .catch(error => {
                    console.log(error.toString())
                    addNotification(`Unable to update the Profile. ${error.toString()}`, NotificationTypes.ERROR, false)
                    return false
                })
                .finally(() => {
                    // must return false to avoid postback
                    return false
                })
            
        }
        catch (error) {
            addNotification(`Unable to update the Profile. ${error.toString()}`, NotificationTypes.ERROR, false)
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }



    if (!profileIsLoaded.value || !schemaIsLoaded.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showCancelModal.value} handleCancel={() => showCancelModal.value = false} handleConfirm={() => navigate('../')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                Profile List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {profile.value.componentProfile.name}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title='Profile Edit JSON' canEditSignal={canEditSignal} />
            <ProfileTools profile={profile.value}></ProfileTools>
        </Stack>
        <ProfileNav beId={beId} profileId={profile.value.componentProfile.id} activeKey="5" debug={debug}></ProfileNav>
        <Form onSubmit={handleFormSubmit}>
            <Form.Group className="mb-3" controlId="formEditJson">
                <Form.Label>Profile JSON</Form.Label>
                <Form.Control as="textarea" rows={profileRows.value} onChange={(e) => editableProfile.value = e.target.value} defaultValue={JSON.stringify(profile.value.componentProfile.config, null, 4)} />
            </Form.Group>
            {canEditSignal.value && <Button variant="primary" type="submit">
                Submit
            </Button>}
        </Form>
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}